import { Message, MemoryContext } from './types';
import { 
  SESSIONS_KEY, 
  DEFAULT_OPENROUTER_API_KEY, 
  DEFAULT_MAX_CYCLES,
  OPENROUTER_API_KEY_STORAGE,
  MAX_CYCLES_STORAGE
} from './config';

// Processing state for tracking DERA cycles
export interface ProcessingState {
  current_cycle: number | null;
  max_cycles: number;
  progress: number;
}

// API settings interface
export interface ApiSettings {
  apiKey: string;
  maxCycles: number;
}

// Define a complete session structure that includes all session-specific state
export interface Session {
  id: string;
  // Core session data
  memoryContext: MemoryContext | null;
  title: string;
  createdAt: number;
  updatedAt: number;

  // Session-specific state
  processingState: ProcessingState | null;
  apiSettings: ApiSettings;
}

// Use SESSIONS_KEY from config

// Get all stored sessions
export const getStoredSessions = (): Session[] => {
  try {
    const sessionsData = localStorage.getItem(SESSIONS_KEY);
    return sessionsData ? JSON.parse(sessionsData) : [];
  } catch (error) {
    console.error('Error getting stored sessions:', error);
    return [];
  }
};

// Save sessions to localStorage
const saveSessionsToStorage = (sessions: Session[]) => {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  } catch (error) {
    console.error('Error saving sessions to storage:', error);
  }
};

// Get a specific session by ID
export const getSession = (sessionId: string): Session | null => {
  const sessions = getStoredSessions();
  const session = sessions.find(s => s.id === sessionId);
  return session || null;
};

// Generate a more unique session ID to avoid collisions
const generateSessionId = (): string => {
  const timestamp = Date.now();
  const randomhex = crypto.randomUUID();
  
  return `session_${timestamp}_${randomhex}`;
};

// Create a new session
export const createSession = (
  title: string, 
  initialMessage: Message,
  initialMemoryContext: MemoryContext | null = null
): Session => {
  console.log(`[SessionStorage] Creating new session with title: ${title}`);
  
  const existingSessions = getStoredSessions();
  
  // Create memory context with initial message if it doesn't exist
  let sessionMemoryContext = initialMemoryContext;
  if (!sessionMemoryContext) {
    sessionMemoryContext = {
      conversation_history: [initialMessage],
      execution_history: []
    };
  } else if (!sessionMemoryContext.conversation_history.find(msg => msg.id === initialMessage.id)) {
    // Add the initial message to the memory context if it's not already there
    sessionMemoryContext = {
      ...sessionMemoryContext,
      conversation_history: [...sessionMemoryContext.conversation_history, initialMessage]
    };
  }
  
  const now = Date.now();
  const sessionId = generateSessionId();
  
  const newSession: Session = {
    id: sessionId,
    memoryContext: sessionMemoryContext,
    title: title.substring(0, 50), // Limit title length
    createdAt: now,
    updatedAt: now,
    // Initialize session-specific state
    processingState: null,
    apiSettings: {
      apiKey: localStorage.getItem(OPENROUTER_API_KEY_STORAGE) || DEFAULT_OPENROUTER_API_KEY,
      maxCycles: Number(localStorage.getItem(MAX_CYCLES_STORAGE)) || DEFAULT_MAX_CYCLES
    }
  };
  
  console.log(`[SessionStorage] Created session with ID: ${sessionId}`);
  
  // Add to beginning of sessions list and save
  const updatedSessions = [newSession, ...existingSessions];
  saveSessionsToStorage(updatedSessions);
  
  return newSession;
};

// Update an existing session
export const updateSession = (
  sessionId: string, 
  updates: Partial<Omit<Session, 'id' | 'createdAt' | 'updatedAt'>>
): void => {
  // Get fresh data
  const sessions = getStoredSessions();
  const sessionIndex = sessions.findIndex(s => s.id === sessionId);
  
  if (sessionIndex !== -1) {
    console.log(`[SessionStorage] Updating session: ${sessionId}`);
    
    // Get the existing session
    const session = sessions[sessionIndex];
    
    // Update the session with new properties
    const updatedSession: Session = {
      ...session,
      ...updates,
      updatedAt: Date.now(),
    };
    
    // Replace the old session with the updated one
    sessions[sessionIndex] = updatedSession;
    saveSessionsToStorage(sessions);
  } else {
    console.error(`[SessionStorage] Tried to update non-existent session: ${sessionId}`);
  }
};

// Update just the memory context (for backward compatibility)
export const updateSessionMemoryContext = (
  sessionId: string, 
  memoryContext: MemoryContext | null = null
): void => {
  updateSession(sessionId, { memoryContext });
};


// Update session processing state
export const updateSessionProcessingState = (
  sessionId: string, 
  processingState: ProcessingState | null
): void => {
  updateSession(sessionId, { processingState });
};

// Update session API settings
export const updateSessionApiSettings = (
  sessionId: string, 
  apiSettings: ApiSettings
): void => {
  updateSession(sessionId, { apiSettings });
};

// Delete a session
export const deleteSession = (sessionId: string): void => {
  console.log(`[SessionStorage] Deleting session: ${sessionId}`);
  const sessions = getStoredSessions();
  const filteredSessions = sessions.filter(s => s.id !== sessionId);
  saveSessionsToStorage(filteredSessions);
};

// Delete all sessions
export const deleteAllSessions = (): void => {
  console.log(`[SessionStorage] Deleting all sessions`);
  saveSessionsToStorage([]);
};

// Update session title
export const updateSessionTitle = (sessionId: string, title: string): void => {
  const sessions = getStoredSessions();
  const sessionIndex = sessions.findIndex(s => s.id === sessionId);
  
  if (sessionIndex !== -1) {
    console.log(`[SessionStorage] Updating title for session: ${sessionId}`);
    sessions[sessionIndex].title = title.substring(0, 50); // Limit title length
    sessions[sessionIndex].updatedAt = Date.now();
    saveSessionsToStorage(sessions);
  } else {
    console.error(`[SessionStorage] Tried to update title for non-existent session: ${sessionId}`);
  }
};

// Import a session from the server data
export const importSession = (
  sessionId: string,
  sessionData: {
    memory_context: MemoryContext;
    title: string;
    created_at: string;
    updated_at: string;
  }
): Session => {
  console.log(`[SessionStorage] Importing session with ID: ${sessionId}`);
  
  const existingSessions = getStoredSessions();
  
  // Check if session already exists locally
  if (existingSessions.find(s => s.id === sessionId)) {
    console.log(`[SessionStorage] Session ${sessionId} already exists locally`);
    // Just return the existing session
    return getSession(sessionId)!;
  }
  
  // Create a new local copy of the session with imported data
  const now = Date.now();
  const newSession: Session = {
    id: sessionId,
    memoryContext: sessionData.memory_context,
    title: sessionData.title,
    createdAt: new Date(sessionData.created_at).getTime(),
    updatedAt: now,
    processingState: null,
    apiSettings: {
      apiKey: localStorage.getItem(OPENROUTER_API_KEY_STORAGE) || DEFAULT_OPENROUTER_API_KEY,
      maxCycles: Number(localStorage.getItem(MAX_CYCLES_STORAGE)) || DEFAULT_MAX_CYCLES
    }
  };
  
  // Add to the beginning of sessions list and save
  const updatedSessions = [newSession, ...existingSessions];
  saveSessionsToStorage(updatedSessions);
  
  return newSession;
};
